// filepath: src/components/Home.jsx
import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Container,
  Box,
  Typography,
  Grid,
  Button,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
// Import motion from framer-motion/dist/framer-motion for better tree-shaking (optional)
import { motion } from "framer-motion"; 
import InsightsIcon from '@mui/icons-material/Insights';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';

// --- ADVANCED ANIMATION VARIANTS ---

// 1. Staggered Container for Sections
const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.2 } }, 
};

// 2. Text Character/Word Fade-in
const textVariant = {
  hidden: { y: "100%", opacity: 0 },
  visible: { y: "0%", opacity: 1, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }, // Custom ease for smooth, world-class feel
};

// 3. General Slide-Up/Fade-In
const fadeInUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

// 4. Scale In for Hero Image/Box
const scaleIn = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: "easeInOut" } },
};

// Feature Icon Map
const featureIcons = {
    optimization: RocketLaunchIcon,
    monitoring: MonitorHeartIcon,
    analytics: InsightsIcon,
};

// Framer Motion Mui Components
const MotionPaper = motion(Paper);
const MotionButton = motion(Button);
const MotionTypography = motion(Typography);
const MotionBox = motion(Box);


const Home = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Split the Hero Title into words for staggered animation
  const title1Words = t("home.hero.title1").split(" ");
  const title2Words = t("home.hero.title2").split(" ");

  const bgAnimationProps = {
    repeat: Infinity,
    duration: isMobile ? 40 : 25, // Slower animation on mobile
    ease: "linear",
  };
  
  const FeatureIcon = (feature) => {
    const IconComponent = featureIcons[feature] || RocketLaunchIcon;
    return <IconComponent sx={{ fontSize: 32 }} />;
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        position: "relative",
        overflowX: "hidden",
      }}
    >
      {/* Background Animated Shapes */}
      {/* Removed the initial/animate props on the main div and applied it directly to the style object for simpler style manipulation */}
      <motion.div
        animate={{ x: ["-100%", "100%"], opacity: [0, 0.3, 0.3, 0] }}
        transition={{ ...bgAnimationProps, times: [0, 0.2, 0.8, 1] }}
        style={{
          position: "absolute",
          top: "20%",
          width: isMobile ? "100px" : "200px",
          height: isMobile ? "100px" : "200px",
          background: "linear-gradient(135deg, #6366f1, #a5b4fc)",
          borderRadius: "50%",
          filter: "blur(70px)",
          zIndex: 0,
        }}
      />
      <motion.div
        animate={{ y: ["100%", "-100%"], opacity: [0, 0.25, 0.25, 0] }}
        transition={{ ...bgAnimationProps, duration: isMobile ? 50 : 35, times: [0, 0.2, 0.8, 1] }}
        style={{
          position: "absolute",
          right: "10%",
          width: isMobile ? "150px" : "300px",
          height: isMobile ? "150px" : "300px",
          background: "linear-gradient(135deg, #ec4899, #f9a8d4)",
          borderRadius: "50%",
          filter: "blur(90px)",
          zIndex: 0,
        }}
      />

      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 14 }, position: "relative", zIndex: 1 }}>
        <Grid container spacing={6} alignItems="center">
          {/* Left Side Text - ADVANCED TEXT ANIMATION */}
          <Grid item xs={12} md={6} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            {/* Wrapper for the Title Animation */}
            <motion.div variants={staggerContainer} initial="hidden" animate="visible">
              <Typography 
                variant={isMobile ? "h4" : "h3"}
                component="h1" 
                fontWeight="bold" 
                gutterBottom
                sx={{ overflow: 'hidden' }} // Crucial for clean text slide-in
              >
                {/* Title Line 1 */}
                <Box component="span" sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' }, flexWrap: 'wrap' }}>
                    {title1Words.map((word, index) => (
                        <MotionBox component="span" variants={textVariant} key={index} sx={{ mr: 1 }}>
                            {word}
                        </MotionBox>
                    ))}
                </Box>
                {/* Title Line 2 */}
                <Box component="span" sx={{ color: theme.palette.primary.main, display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' }, flexWrap: 'wrap' }}>
                    {title2Words.map((word, index) => (
                        <MotionBox component="span" variants={textVariant} key={index} sx={{ mr: 1 }}>
                            {word}
                        </MotionBox>
                    ))}
                </Box>
              </Typography>
            </motion.div>
            
            {/* Description and CTA - Simple FadeInUp (staggered delay) */}
            <motion.div variants={fadeInUp} initial="hidden" animate="visible" transition={{ delay: 0.8 }}>
              <Typography 
                variant={isMobile ? "body1" : "h6"}
                color="text.secondary" 
                sx={{ mb: 4 }}
              >
                {t("home.hero.description")}
              </Typography>
            </motion.div>
            
            {/* Button Box - MotionButton for fluid hover effects */}
            <Box 
                sx={{ 
                    display: "flex", 
                    gap: 2, 
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'center', md: 'flex-start' },
                    '& > *': { width: { xs: '100%', sm: 'auto' } }
                }}
            >
                <MotionButton
                    component={Link}
                    to="/features"
                    variant="contained"
                    color="primary"
                    size="large"
                    whileHover={{ scale: 1.05, boxShadow: "0 8px 15px rgba(99, 102, 241, 0.4)" }}
                    whileTap={{ scale: 0.95 }}
                >
                    {t("home.cta.getStarted")}
                </MotionButton>
                <MotionButton
                    component={Link}
                    to="/contact"
                    variant="outlined"
                    color="primary"
                    size="large"
                    whileHover={{ scale: 1.05, backgroundColor: theme.palette.primary.light, color: 'white' }}
                    whileTap={{ scale: 0.95 }}
                >
                    {t("home.cta.contactSales")}
                </MotionButton>
            </Box>
          </Grid>

          {/* Right Side Image/Box */}
          <Grid item xs={12} md={6} sx={{ order: { xs: -1, md: 0 } }}>
            <motion.div variants={scaleIn} initial="hidden" animate="visible">
              <Box
                sx={{
                  width: "100%",
                  height: { xs: 200, sm: 280, md: 350 },
                  borderRadius: 4,
                  background:
                    "linear-gradient(135deg, #6366f1, #ec4899, #fbbf24)",
                  boxShadow: 8,
                }}
              />
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* Features Section */}
      <Box sx={{ py: { xs: 8, md: 14 }, bgcolor: "background.paper" }}>
        <Container maxWidth="lg">
            {/* Added a title here for context/structure */}
            <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.5 }} sx={{ textAlign: 'center', mb: 6 }}>
                <Typography variant="h5" color="primary.main" fontWeight="medium">
                    {t("home.features.subtitle")}
                </Typography>
                <Typography variant="h3" fontWeight="bold">
                    {t("home.features.mainTitle")}
                </Typography>
            </motion.div>
          
          {/* Feature Grid - Scroll Reveal and Advanced Hover */}
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
            <Grid container spacing={4}>
              {["optimization", "monitoring", "analytics"].map((feature, idx) => (
                <Grid item xs={12} sm={6} md={4} key={idx}>
                  <motion.div variants={fadeInUp}>
                    <MotionPaper // Use MotionPaper for Framer Motion effects
                      elevation={3}
                      whileHover={{ 
                          scale: 1.03, 
                          boxShadow: "0 10px 20px rgba(0, 0, 0, 0.15)",
                          translateY: -5 
                      }}
                      whileTap={{ scale: 0.98 }}
                      sx={{
                        p: { xs: 3, md: 4 },
                        borderRadius: 3,
                        minHeight: 200,
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer', // Indicate interactivity
                      }}
                    >
                      <Box sx={{ color: "primary.main", mb: 2 }}>
                        {FeatureIcon(feature)}
                      </Box>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {t(`home.features.${feature}.title`)}
                      </Typography>
                      <Typography color="text.secondary">
                        {t(`home.features.${feature}.description`)}
                      </Typography>
                    </MotionPaper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* CTA Section (Bottom) */}
      <MotionBox 
        variants={fadeInUp} 
        initial="hidden" 
        whileInView="visible" 
        viewport={{ once: true }} 
        sx={{ py: { xs: 8, md: 14 }, bgcolor: "primary.main", color: "white" }}
      >
        <Container
          maxWidth="lg"
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            textAlign: { xs: 'center', md: 'left' }
          }}
        >
            <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" gutterBottom>
              <span>{t("home.ctaBottom.title1")}</span>
              <span style={{ color: "#c7d2fe", display: "block" }}>
                {t("home.ctaBottom.title2")}
              </span>
            </Typography>
            <MotionButton
                component={Link}
                to="/dashboard"
                variant="contained"
                color="secondary"
                size="large"
                whileHover={{ scale: 1.05, boxShadow: "0 8px 15px rgba(255, 255, 255, 0.3)" }}
                whileTap={{ scale: 0.95 }}
                sx={{ 
                    mt: { xs: 4, md: 0 },
                    width: { xs: '100%', sm: 'auto' }
                }}
            >
                {t("home.ctaBottom.button")}
            </MotionButton>
        </Container>
      </MotionBox>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 14 } }}>
        <Grid container spacing={4}>
          {/* Stat values are not dynamic, so simple stagger will suffice */}
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.5 }}>
            <Grid container spacing={4}>
                {[
                    { value: "%", label: t("home.stats.containers") },
                    { value: "100+", label: t("home.stats.images") },
                    { value: "95%", label: t("home.stats.alerts") },
                    { value: "24/7", label: t("home.stats.uptime") },
                ].map((stat, idx) => (
                    <Grid item xs={6} md={3} textAlign="center" key={idx}>
                    <motion.div 
                        variants={fadeInUp} 
                        // The delay is now handled by staggerContainer
                    >
                        <Typography variant={isMobile ? "h4" : "h3"} fontWeight="bold" color="primary.main">
                        {stat.value}
                        </Typography>
                        <Typography color="text.secondary" variant={isMobile ? "body2" : "body1"}>
                        {stat.label}
                        </Typography>
                    </motion.div>
                    </Grid>
                ))}
            </Grid>
          </motion.div>
        </Grid>
      </Container>
    </Box>
  );
};

export default Home;
